import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { operacaoApi } from '../api/endpoints';
import { resolveBaseUrl, SESSION_EXPIRED_EVENT } from '../api/client';
import type { LoginResponse, Order, ServiceRequest } from '../types/domain';
import { registerStaffWorker, withDeadline } from './serviceWorker';

export type StaffActionKind = 'claim' | 'claim_approve' | 'approve' | 'register';
export interface StaffAction {
  id: string;
  orderId: number;
  mesa: number;
  kind: StaffActionKind;
  expected: Pick<Order, 'estado' | 'assumido_por_nome' | 'registado_em'>;
}
export interface QueuedStaffAction extends StaffAction {
  scope: string;
  createdAt: number;
  status: 'pending' | 'conflict';
  error?: string;
}
export interface StaffSnapshot {
  orders?: Order[];
  calls?: ServiceRequest[];
  ordersUpdated?: number;
  callsUpdated?: number;
}
export interface StaffOfflineStatus {
  actions: QueuedStaffAction[];
  snapshot: StaffSnapshot | null;
  offlineReady: boolean;
}
interface OfflineSession { token: string; user: string; tenant: string; apiBase: string }
const empty: StaffOfflineStatus = { actions: [], snapshot: null, offlineReady: false };
const preparationError = (cause: unknown) => {
  const error = cause as { name?: string; message?: string } | null;
  if (/certificate|certificado|\bSSL\b|\bTLS\b/i.test(error?.message ?? '')) {
    return 'O navegador não confia no certificado desta ligação. Instala o certificado local do Bamidi nas definições do dispositivo e reabre a app sem avisos de segurança.';
  }
  if (error?.name === 'SecurityError' || error?.name === 'NotAllowedError') {
    return 'O navegador bloqueou o modo offline. Confirma que a ligação HTTPS abre sem avisos e que o navegador permite guardar dados deste site.';
  }
  // Older installed workers only return the message; keep their quota advice useful.
  if (error?.name === 'QuotaExceededError' || /quota.*exceed/i.test(error?.message ?? '')) {
    return 'Não há espaço disponível para preparar o modo offline. Liberta espaço no dispositivo e tenta novamente.';
  }
  return 'Não foi possível preparar o modo offline. Mantém a ligação e tenta preparar novamente.';
};
const newActionId = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 15) | 64; bytes[8] = (bytes[8]! & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
};
export const actionLabels: Record<StaffActionKind, string> = {
  claim: 'Assumir pedido', claim_approve: 'Assumir e aprovar', approve: 'Aprovar pedido', register: 'Confirmar registo',
};

export async function staffWorkerRequest<T>(type: string, payload: object = {}): Promise<T> {
  const registration = await registerStaffWorker();
  const worker = registration.active ?? navigator.serviceWorker.controller;
  if (!worker) throw new Error('A app ainda não está preparada para guardar ações offline.');
  const channel = new MessageChannel();
  try {
    return await withDeadline(new Promise<T>((resolve, reject) => {
      channel.port1.onmessage = (event: MessageEvent<{ ok: boolean; value: T; error?: string; errorName?: string }>) => {
        if (event.data.ok) resolve(event.data.value);
        else {
          const error = new Error(event.data.error || 'Não foi possível guardar a ação neste dispositivo.');
          error.name = event.data.errorName || 'Error';
          reject(error);
        }
      };
      worker.postMessage({ type, ...payload }, [channel.port2]);
    }), 'Não foi possível comunicar com o armazenamento da app. Tenta novamente.');
  } finally { channel.port1.close(); channel.port2.close(); }
}

export async function suspendStaffOffline() {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return;
  await staffWorkerRequest('STAFF_LOGOUT').catch(() => undefined);
}

/** Project only reversible UI state; a queued registration stays visible until confirmed. */
export function projectStaffOrders(orders: Order[], actions: QueuedStaffAction[], username: string): Order[] {
  const conflicts = new Set(actions.filter((action) => action.status === 'conflict').map((action) => action.orderId));
  return orders.filter((order) => !order.assumido_por || order.assumido_por_nome === username).map((original) => {
    let order = original;
    for (const action of actions.filter((item) => item.orderId === order.id && !conflicts.has(item.orderId))) {
      if (action.kind === 'approve' && order.assumido_por_nome === username) order = { ...order, estado: 'P' };
    }
    return order;
  });
}

export function useStaffOffline(sessao: LoginResponse | null, onSynced: () => void) {
  const session = useMemo<OfflineSession | null>(() => sessao?.token && sessao.tenants[0] ? {
    user: sessao.user, token: sessao.token, tenant: sessao.tenants[0].slug, apiBase: resolveBaseUrl(),
  } : null, [sessao?.user, sessao?.token, sessao?.tenants[0]?.slug]);
  const scope = session ? JSON.stringify([session.tenant, session.user]) : '';
  const [state, setState] = useState<{ scope: string; status: StaffOfflineStatus }>({ scope: '', status: empty });
  const [connectedScope, setConnectedScope] = useState('');
  const [storageError, setStorageError] = useState('');
  const [preparing, setPreparing] = useState(false);
  const [preparationAttempt, setPreparationAttempt] = useState(0);
  const latestSync = useRef(onSynced); latestSync.current = onSynced;
  const supported = Boolean(session && window.isSecureContext && 'serviceWorker' in navigator);
  const status = state.scope === scope ? state.status : empty;
  const available = Boolean(scope && connectedScope === scope);
  const request = useCallback(async (type: string, payload: object = {}) => {
    const next = await staffWorkerRequest<StaffOfflineStatus>(type, { session, ...payload });
    setState({ scope, status: next });
    if (next.offlineReady) setStorageError('');
    return next;
  }, [session, scope]);

  useEffect(() => {
    if (!supported) return;
    let active = true;
    let connecting = false;
    let prepared = false;
    setConnectedScope('');
    const connect = async () => {
      if (!active || connecting) return;
      connecting = true;
      setPreparing(true);
      try {
        const next = await staffWorkerRequest<StaffOfflineStatus>('STAFF_CONNECT', { session });
        if (active) { setState({ scope, status: next }); setConnectedScope(scope); setStorageError(''); }
        prepared = next.offlineReady;
        if (active && !next.offlineReady && navigator.onLine) {
          const resources = performance.getEntriesByType('resource').map((resource) => resource.name);
          const nextPrepared = await staffWorkerRequest<StaffOfflineStatus>('STAFF_PREPARE', { session, resources });
          prepared = nextPrepared.offlineReady;
          if (active) setState({ scope, status: nextPrepared });
        }
      } catch (cause) { if (active) setStorageError(preparationError(cause)); }
      finally { connecting = false; if (active) setPreparing(false); }
    };
    const sync = () => {
      if (!navigator.onLine) return;
      if (!prepared) { void connect(); return; }
      void request('STAFF_SYNC').catch(() => undefined);
    };
    const visible = () => { if (document.visibilityState === 'visible') sync(); };
    const changed = (event: MessageEvent) => {
      if (!active || event.data?.type !== 'STAFF_OFFLINE_CHANGED' || event.data.scope !== scope) return;
      if (event.data.authExpired) { window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT)); return; }
      void request('STAFF_STATUS').catch(() => undefined);
      if (event.data.synced) latestSync.current();
    };
    void connect();
    const timer = window.setInterval(sync, 20_000);
    window.addEventListener('online', sync); document.addEventListener('visibilitychange', visible);
    navigator.serviceWorker.addEventListener('message', changed);
    navigator.serviceWorker.addEventListener('controllerchange', connect);
    return () => {
      active = false; window.clearInterval(timer);
      window.removeEventListener('online', sync); document.removeEventListener('visibilitychange', visible);
      navigator.serviceWorker.removeEventListener('message', changed);
      navigator.serviceWorker.removeEventListener('controllerchange', connect);
    };
  }, [supported, session, scope, request, preparationAttempt]);

  const saveSnapshot = useCallback(async (data: StaffSnapshot) => {
    if (!available) return;
    try { await request('STAFF_SNAPSHOT', { data }); }
    catch { setStorageError('Não foi possível guardar os últimos pedidos neste dispositivo. O modo offline pode estar desatualizado.'); }
  }, [available, request]);
  const submit = async (order: Order, kind: StaffActionKind) => {
    const action: StaffAction = {
      id: newActionId(), kind, orderId: order.id, mesa: order.mesa,
      expected: { estado: order.estado, assumido_por_nome: order.assumido_por_nome, registado_em: order.registado_em },
    };
    // A reservation must reach the server before this account can treat the order.
    if (kind === 'claim' || kind === 'claim_approve') {
      if (!navigator.onLine) throw new Error('Liga-te à rede para assumir o pedido e reservá-lo para a tua conta.');
      await operacaoApi.acaoOffline(action);
      return { queued: false };
    }
    if (available) { await request('STAFF_ENQUEUE', { action }); return { queued: true }; }
    if (!navigator.onLine) throw new Error('O armazenamento offline não está disponível. Liga-te à rede antes de continuar.');
    // Devices without service workers still use the same idempotent server contract.
    await operacaoApi.acaoOffline(action);
    return { queued: false };
  };
  return {
    ...status, available, storageError, preparing, submit, saveSnapshot,
    prepare: () => setPreparationAttempt((attempt) => attempt + 1),
    sync: () => request('STAFF_SYNC'), dismiss: (id: string) => request('STAFF_DISMISS', { id }),
  };
}
